import { MaterialCommunityIcons } from '@expo/vector-icons';
import {
  useCreateMutation,
  useCurrentWorkspace,
  useDeleteMutation,
  useFindByQuery,
  useUpdateMutation,
  useWorkspaceCategories,
} from '@gripp/shared-logic';
import {
  CategorySelector,
  Colors,
  ControlledTextInput,
  CustomAlert,
  SaveFormTop,
  TopModalBar,
} from '@gripp/shared-ui';
import { yupResolver } from '@hookform/resolvers/yup';
import { useAudioPlayer } from 'expo-audio';
import React, { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import Toast from 'react-native-toast-message';
import * as yup from 'yup';

// eslint-disable-next-line @typescript-eslint/no-var-requires
const audioSource = require('../../../static-assets/delete_pop.mp3');

const CREATE_CATEGORY = `
  mutation createCategory($input: CreateCategoryInput!) {
    createCategory(input: $input) {
      id
      name
    }
  }
`;

type Category = {
  id: string;
  name: string;
};

type EditFileFormProps = {
  onComplete: () => void;
  fileId: string;
};

export const EditFileForm = ({
  onComplete,
  fileId,
}: EditFileFormProps) => {
  const { t } = useTranslation();
  const currentWorkspace = useCurrentWorkspace();

  const filter = useMemo(() => {
    return {
      id: { eq: fileId },
    };
  }, [fileId]);

  const { data, isLoading: isFileLoading } = useFindByQuery({
    modelName: 'media',
    query: FIND_FILE,
    variables: { filter },
  });

  const { categories } = useWorkspaceCategories();

  const { mutateAsync: createCategory, isPending: isCreatingCategory } =
    useCreateMutation({
      modelName: 'category',
      query: CREATE_CATEGORY,
    });

  const [fileName, setFileName] = useState('');
  const [fileCategories, setFileCategories] = useState<Category[]>([]);
  const [showDeleteAlert, setShowDeleteAlert] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const player = useAudioPlayer(audioSource);

  const { mutateAsync } = useUpdateMutation({
    modelName: 'media',
    query: UPDATE_FILE,
  });

  const { mutateAsync: deleteFileAsync } = useDeleteMutation({
    modelName: 'media',
    query: DELETE_FILE,
  });

  const formSchema = yup.object({
    name: yup.string().required('File name is required'),
    categories: yup
      .array()
      .of(
        yup.object({
          id: yup.string().required(),
          name: yup.string().required(),
        })
      )
      .optional(),
    newCategoryName: yup.string().optional(),
  });

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { isDirty },
  } = useForm({
    resolver: yupResolver(formSchema),
    mode: 'onSubmit',
    defaultValues: {
      name: fileName,
      categories: fileCategories,
      newCategoryName: '',
    },
  });

  const selectedCategories = watch('categories') || [];

  useEffect(() => {
    if (!data || !data.items.length) return;

    const file = data.items[0];
    reset({
      name: file.name,
      categories: file.categories || [],
      newCategoryName: '',
    });
    setFileName(file.name);
    setFileCategories(file.categories || []);
  }, [data, reset]);

  const handleCreateCategory = async (name: string) => {
    try {
      const result = await createCategory({
        input: {
          name: name.trim(),
          workspace: { id: currentWorkspace.id },
        },
      });

      if (result?.id) {
        return {
          id: result.id,
          name: result.name,
        };
      }
      throw new Error('Failed to create category');
    } catch (error) {
      console.error('Failed to create category:', error);
      throw error;
    }
  };

  const closeCategoryModal = () => {
    setIsModalOpen(false);
    // Clear the new category name field
    setValue('newCategoryName', '', { shouldDirty: false });
  };

  const onSubmit = async (data: yup.InferType<typeof formSchema>) => {
    const input = {
      id: fileId,
      name: data.name,
      categories: data.categories?.map((c) => ({ id: c.id })) || [],
    };

    await mutateAsync(
      { input },
      {
        onSettled: () => {
          onComplete();
        },
      }
    );
  };

  const onFileDelete = async () => {
    const input = {
      id: fileId,
    };

    await deleteFileAsync({ input });

    onComplete();
    showToast();
    player.play();
  };

  const showToast = () => {
    Toast.show({
      type: 'blackToast',
      text1: 'Woosh!',
      text2: `${fileName} has been deleted.`,
      position: 'bottom',
      autoHide: true,
      visibilityTime: 2500,
    });
  };

  return (
    !isFileLoading && (
      <>
        <View style={{ flex: 1, width: '100%' }}>
          <SaveFormTop
            onSubmit={handleSubmit(onSubmit)}
            title={fileName}
            topBar={TopModalBar}
            isDirty={isDirty}
            onCancel={onComplete}
          >
            <View style={styles.innerFormContainer}>
              <ControlledTextInput
                control={control}
                name="name"
                placeholder="File Name"
              />
              <View style={styles.categoriesContainer}>
                <CategorySelector
                  selectedCategories={selectedCategories}
                  availableCategories={categories}
                  onCategoriesChange={(newCategories) => {
                    setValue('categories', newCategories, {
                      shouldDirty: true,
                      shouldValidate: true,
                    });
                  }}
                  onCreateCategory={handleCreateCategory}
                />
              </View>
            </View>
            <View style={styles.bottomContainer}>
              <Pressable
                style={styles.deleteButton}
                onPress={() => {
                  setShowDeleteAlert(true);
                }}
              >
                <Text style={styles.deleteButtonLabel}>
                  {t('file.deleteFile.deleteFile')}
                </Text>
              </Pressable>
            </View>
          </SaveFormTop>
        </View>

        {/* Create Category Modal */}
        {isModalOpen && (
          <View style={styles.modalOverlay}>
            <View style={styles.modalContainer}>
              <View style={styles.modalHeader}>
                <Text style={styles.modalTitle}>
                  Create Category
                </Text>
                <Pressable onPress={closeCategoryModal}>
                  <MaterialCommunityIcons
                    name="close"
                    size={24}
                    color={Colors.blackText}
                  />
                </Pressable>
              </View>
              <View style={styles.modalContent}>
                <ControlledTextInput
                  control={control}
                  name="newCategoryName"
                  placeholder="Enter category name"
                  key="new-category-input"
                />
                <View style={styles.modalButtons}>
                  <Pressable
                    style={styles.cancelButton}
                    onPress={closeCategoryModal}
                  >
                    <Text style={styles.cancelButtonText}>Cancel</Text>
                  </Pressable>
                  <Pressable
                    style={styles.createButton}
                    onPress={() => {
                      const newName = watch('newCategoryName');
                      if (
                        newName &&
                        typeof newName === 'string' &&
                        newName.trim()
                      ) {
                        handleCreateCategory(newName);
                      }
                    }}
                    disabled={isCreatingCategory}
                  >
                    <Text style={styles.createButtonText}>
                      {isCreatingCategory ? 'Creating...' : 'Create'}
                    </Text>
                  </Pressable>
                </View>
              </View>
            </View>
          </View>
        )}

        <CustomAlert
          visible={showDeleteAlert}
          hideAlert={() => setShowDeleteAlert(false)}
          title={
            <>
              {t('file.deleteFile.alertBody1')} <b>{fileName}</b>?{' '}
            </>
          }
          primaryButtonLabel={t('file.deleteFile.delete')}
          secondaryButtonLabel={t('file.deleteFile.cancel')}
          action={() => onFileDelete()}
          containerStyle={styles.deleteAlertContainer}
        />
      </>
    )
  );
};

const styles = StyleSheet.create({
  innerFormContainer: {
    margin: 20,
  },
  categoriesContainer: {
    marginTop: 16,
  },
  bottomContainer: {
    flex: 1,
    borderTopWidth: 1,
    borderTopColor: Colors.graySpacer,
    flexDirection: 'row',
    alignItems: 'center',
    paddingLeft: 20,
    paddingVertical: 20,
  },
  deleteButton: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  deleteButtonLabel: {
    fontSize: 16,
    fontWeight: '600',
    letterSpacing: -0.25,
    color: Colors.danger,
  },
  deleteAlertContainer: {
    width: 350,
  },
  addNewButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 10,
    paddingHorizontal: 15,
    backgroundColor: Colors.primaryWithAlpha,
    borderRadius: 8,
    marginTop: 10,
  },
  buttonLabel: {
    marginLeft: 8,
    fontSize: 14,
    fontWeight: '600',
    color: Colors.primaryDark,
  },
  modalOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
  },
  modalContainer: {
    backgroundColor: Colors.white,
    borderRadius: 10,
    width: '80%',
    maxWidth: 400,
    padding: 20,
    alignItems: 'center',
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    marginBottom: 15,
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: Colors.blackText,
  },
  modalContent: {
    width: '100%',
    marginBottom: 20,
  },
  modalButtons: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    width: '100%',
  },
  cancelButton: {
    paddingVertical: 10,
    paddingHorizontal: 20,
    backgroundColor: Colors.secondaryGrayHeader,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: Colors.graySpacer,
  },
  cancelButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: Colors.blackText,
  },
  createButton: {
    paddingVertical: 10,
    paddingHorizontal: 20,
    backgroundColor: Colors.primary,
    borderRadius: 8,
  },
  createButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: Colors.white,
  },
});

const UPDATE_FILE = `
  mutation UpdateFile($input: MutateMediaInput!) {
    updateMedia(input: $input) {
      id
    }
  }
`;

const DELETE_FILE = `
  mutation DeleteFile($input: MutateMediaInput!) {
    deleteMedia(input: $input) {
      id
    }
  }
`;

const FIND_FILE = `
  query FindFiles($filter: MediaFilter) {
    findMedia(filter: $filter) {
      items {
        id
        name
        categories {
          id
          name
        }
      }
    }
  }
`;
